<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TaxiZonesAPI from 'dashboard/api/taxiZones';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import FranceZonesMap from './FranceZonesMap.vue';

const { t } = useI18n();

// Champs multi-valeurs : saisie explicite (TagInput + allowCreate pouvait ne rien envoyer au v-model)
const MULTI_FIELD_KEYS = ['cities', 'districts', 'countries', 'postal_codes'];

const pendingMulti = reactive({
  cities: '',
  districts: '',
  countries: '',
  postal_codes: '',
});

const zones = ref([]);
const loading = ref(false);
const searchQuery = ref('');
const zoneDialogRef = ref(null);
const editing = ref(null);

const form = ref({
  name: '',
  cities: [],
  districts: [],
  countries: [],
  postal_codes: [],
});

const clearPendingMulti = () => {
  MULTI_FIELD_KEYS.forEach(key => {
    pendingMulti[key] = '';
  });
};

const addPendingValue = key => {
  const raw = pendingMulti[key]?.trim();
  if (!raw) return;
  const list = form.value[key];
  if (!list.includes(raw)) {
    list.push(raw);
  }
  pendingMulti[key] = '';
};

const removeMultiValue = (key, index) => {
  form.value[key].splice(index, 1);
};

const multiFieldBlocks = computed(() => [
  {
    key: 'cities',
    label: t('TAXIS.ZONES.FORM.CITIES'),
    placeholder: t('TAXIS.ZONES.FORM.CITIES_PLACEHOLDER'),
  },
  {
    key: 'districts',
    label: t('TAXIS.ZONES.FORM.DISTRICTS'),
    placeholder: t('TAXIS.ZONES.FORM.DISTRICTS_PLACEHOLDER'),
  },
  {
    key: 'countries',
    label: t('TAXIS.ZONES.FORM.COUNTRIES'),
    placeholder: t('TAXIS.ZONES.FORM.COUNTRIES_PLACEHOLDER'),
  },
  {
    key: 'postal_codes',
    label: t('TAXIS.ZONES.FORM.POSTAL_CODES'),
    placeholder: t('TAXIS.ZONES.FORM.POSTAL_CODES_PLACEHOLDER'),
  },
]);

const filteredZones = computed(() => {
  if (!searchQuery.value.trim()) return zones.value;
  const q = searchQuery.value.toLowerCase();
  return zones.value.filter(z => {
    const inName = z.name?.toLowerCase().includes(q);
    const inLists = ['cities', 'districts', 'countries', 'postal_codes'].some(
      key => (z[key] || []).some(item => item.toLowerCase().includes(q))
    );
    return inName || inLists;
  });
});

const resetForm = () => {
  form.value = {
    name: '',
    cities: [],
    districts: [],
    countries: [],
    postal_codes: [],
  };
  clearPendingMulti();
  editing.value = null;
};

const fetchZones = async () => {
  loading.value = true;
  try {
    const { data } = await TaxiZonesAPI.getAll();
    zones.value = data;
  } catch {
    useAlert(t('TAXIS.ZONES.API.ERROR_FETCH'));
  } finally {
    loading.value = false;
  }
};

onMounted(fetchZones);

const openCreate = () => {
  resetForm();
  zoneDialogRef.value?.open();
};

const openEdit = zone => {
  editing.value = zone;
  form.value = {
    name: zone.name || '',
    cities: [...(zone.cities || [])],
    districts: [...(zone.districts || [])],
    countries: [...(zone.countries || [])],
    postal_codes: [...(zone.postal_codes || [])],
  };
  clearPendingMulti();
  zoneDialogRef.value?.open();
};

const onDialogClose = () => {
  resetForm();
};

const closeDialog = () => {
  zoneDialogRef.value?.close();
};

const flushPendingIntoForm = () => {
  MULTI_FIELD_KEYS.forEach(key => {
    const raw = pendingMulti[key]?.trim();
    if (!raw) return;
    if (!form.value[key].includes(raw)) {
      form.value[key].push(raw);
    }
    pendingMulti[key] = '';
  });
};

const saveZone = async () => {
  if (!form.value.name.trim()) {
    useAlert(t('TAXIS.ZONES.VALIDATION.NAME_REQUIRED'));
    return;
  }
  flushPendingIntoForm();
  try {
    const payload = {
      name: form.value.name.trim(),
      cities: [...form.value.cities],
      districts: [...form.value.districts],
      countries: [...form.value.countries],
      postal_codes: [...form.value.postal_codes],
    };
    if (editing.value) {
      await TaxiZonesAPI.update(editing.value.id, payload);
      useAlert(t('TAXIS.ZONES.API.SUCCESS_UPDATE'));
    } else {
      await TaxiZonesAPI.create(payload);
      useAlert(t('TAXIS.ZONES.API.SUCCESS_CREATE'));
    }
    closeDialog();
    await fetchZones();
  } catch {
    useAlert(t('TAXIS.ZONES.API.ERROR_SAVE'));
  }
};

const removeZone = async zone => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm(t('TAXIS.ZONES.CONFIRM_DELETE'))) return;
  try {
    await TaxiZonesAPI.delete(zone.id);
    useAlert(t('TAXIS.ZONES.API.SUCCESS_DELETE'));
    await fetchZones();
  } catch {
    useAlert(t('TAXIS.ZONES.API.ERROR_DELETE'));
  }
};

const cellTitle = arr => (arr && arr.length ? arr.join(', ') : '');
</script>

<template>
  <div class="flex flex-1 flex-col overflow-auto px-6 py-8">
    <div class="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-6">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div class="flex items-center gap-3">
            <div
              class="flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-n-amber-4/50 to-n-amber-5/40 ring-1 ring-n-amber-6/60"
            >
              <Icon icon="i-lucide-map" class="size-5 text-n-amber-11" />
            </div>
            <h1 class="text-xl font-semibold text-n-slate-12">
              {{ t('TAXIS.ZONES.TITLE') }}
            </h1>
          </div>
          <p class="mt-2 text-sm text-n-slate-11">
            {{ t('TAXIS.ZONES.SUBTITLE') }}
          </p>
        </div>
        <Button
          :label="t('TAXIS.ZONES.ADD_BUTTON')"
          icon="i-lucide-plus"
          size="sm"
          color="amber"
          @click="openCreate"
        />
      </div>

      <div class="max-w-sm">
        <Input
          v-model="searchQuery"
          type="search"
          :placeholder="t('TAXIS.ZONES.SEARCH_PLACEHOLDER')"
          :custom-input-class="[
            'h-9 [&:not(.focus)]:!border-transparent [&:not(.focus)]:!outline-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-9',
          ]"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="pointer-events-none absolute top-1/2 size-4 -translate-y-1/2 text-n-slate-11 ltr:left-2.5 rtl:right-2.5"
            />
          </template>
        </Input>
      </div>

      <div
        v-if="loading"
        class="flex flex-1 items-center justify-center py-20 text-n-slate-11"
      >
        <Spinner />
      </div>

      <template v-else>
        <FranceZonesMap
          v-if="zones.length"
          class="w-full"
          :zones="filteredZones"
        />

        <div
          v-if="!zones.length"
          class="flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed border-n-amber-5/30 bg-gradient-to-br from-n-amber-1/30 to-transparent py-20 text-center"
        >
          <div
            class="flex size-20 items-center justify-center rounded-2xl bg-gradient-to-br from-n-amber-4/40 to-n-amber-5/30 ring-4 ring-n-amber-4/20"
          >
            <Icon icon="i-lucide-map" class="size-10 text-n-amber-11" />
          </div>
          <div>
            <p class="text-lg font-semibold text-n-slate-12">
              {{ t('TAXIS.ZONES.EMPTY_TITLE') }}
            </p>
            <p class="mt-2 max-w-sm text-sm text-n-slate-11">
              {{ t('TAXIS.ZONES.EMPTY_SUBTITLE') }}
            </p>
          </div>
          <Button
            :label="t('TAXIS.ZONES.ADD_BUTTON')"
            icon="i-lucide-plus"
            size="md"
            color="amber"
            @click="openCreate"
          />
        </div>

        <div
          v-if="zones.length"
          class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
        >
          <div class="overflow-x-auto">
            <table class="w-full min-w-[880px] text-left text-sm">
              <thead
                class="border-b-2 border-n-amber-6/30 bg-gradient-to-r from-n-amber-2/40 to-n-amber-3/20 text-xs font-semibold uppercase tracking-wide text-n-slate-12"
              >
                <tr>
                  <th class="px-4 py-3.5">
                    <div class="flex items-center gap-1.5">
                      <Icon
                        icon="i-lucide-map"
                        class="size-3.5 text-n-amber-10"
                      />
                      {{ t('TAXIS.ZONES.COLUMNS.NAME') }}
                    </div>
                  </th>
                  <th class="px-4 py-3.5">
                    <div class="flex items-center gap-1.5">
                      <Icon
                        icon="i-lucide-building"
                        class="size-3.5 text-n-blue-10"
                      />
                      {{ t('TAXIS.ZONES.COLUMNS.CITIES') }}
                    </div>
                  </th>
                  <th class="px-4 py-3.5">
                    <div class="flex items-center gap-1.5">
                      <Icon
                        icon="i-lucide-map-pin"
                        class="size-3.5 text-n-purple-10"
                      />
                      {{ t('TAXIS.ZONES.COLUMNS.DISTRICTS') }}
                    </div>
                  </th>
                  <th class="px-4 py-3.5">
                    <div class="flex items-center gap-1.5">
                      <Icon
                        icon="i-lucide-globe"
                        class="size-3.5 text-n-green-10"
                      />
                      {{ t('TAXIS.ZONES.COLUMNS.COUNTRIES') }}
                    </div>
                  </th>
                  <th class="px-4 py-3.5">
                    <div class="flex items-center gap-1.5">
                      <Icon
                        icon="i-lucide-mail"
                        class="size-3.5 text-n-teal-10"
                      />
                      {{ t('TAXIS.ZONES.COLUMNS.POSTAL_CODES') }}
                    </div>
                  </th>
                  <th class="px-4 py-3.5 text-end">
                    {{ t('TAXIS.ZONES.COLUMNS.ACTIONS') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr
                  v-for="zone in filteredZones"
                  :key="zone.id"
                  class="transition-colors hover:bg-gradient-to-r hover:from-n-amber-2/10 hover:to-transparent"
                >
                  <td class="px-4 py-3.5">
                    <div class="flex items-center gap-2">
                      <div
                        class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-n-amber-4/40 to-n-amber-5/30 ring-1 ring-n-amber-6/50"
                      >
                        <Icon
                          icon="i-lucide-map"
                          class="size-4 text-n-amber-11"
                        />
                      </div>
                      <span class="font-semibold text-n-slate-12">{{
                        zone.name
                      }}</span>
                    </div>
                  </td>
                  <td class="px-4 py-3.5">
                    <div
                      v-if="zone.cities?.length"
                      class="flex flex-wrap gap-1"
                    >
                      <span
                        v-for="(city, idx) in zone.cities.slice(0, 2)"
                        :key="`city-${idx}`"
                        class="inline-flex items-center gap-1 rounded-md bg-n-blue-2/40 px-2 py-0.5 text-xs text-n-blue-11"
                      >
                        <Icon icon="i-lucide-building" class="size-3" />
                        {{ city }}
                      </span>
                      <span
                        v-if="zone.cities.length > 2"
                        class="inline-flex items-center rounded-md bg-n-blue-2/40 px-2 py-0.5 text-xs font-medium text-n-blue-11"
                        :title="cellTitle(zone.cities)"
                      >
                        {{
                          t('TAXIS.COMMON.MORE_COUNT', {
                            count: zone.cities.length - 2,
                          })
                        }}
                      </span>
                    </div>
                    <span v-else class="text-n-slate-10">{{
                      t('TAXIS.HISTORY.DASH')
                    }}</span>
                  </td>
                  <td class="px-4 py-3.5">
                    <div
                      v-if="zone.districts?.length"
                      class="flex flex-wrap gap-1"
                    >
                      <span
                        v-for="(district, idx) in zone.districts.slice(0, 2)"
                        :key="`district-${idx}`"
                        class="inline-flex items-center gap-1 rounded-md bg-n-purple-2/40 px-2 py-0.5 text-xs text-n-purple-11"
                      >
                        <Icon icon="i-lucide-map-pin" class="size-3" />
                        {{ district }}
                      </span>
                      <span
                        v-if="zone.districts.length > 2"
                        class="inline-flex items-center rounded-md bg-n-purple-2/40 px-2 py-0.5 text-xs font-medium text-n-purple-11"
                        :title="cellTitle(zone.districts)"
                      >
                        {{
                          t('TAXIS.COMMON.MORE_COUNT', {
                            count: zone.districts.length - 2,
                          })
                        }}
                      </span>
                    </div>
                    <span v-else class="text-n-slate-10">{{
                      t('TAXIS.HISTORY.DASH')
                    }}</span>
                  </td>
                  <td class="px-4 py-3.5">
                    <div
                      v-if="zone.countries?.length"
                      class="flex flex-wrap gap-1"
                    >
                      <span
                        v-for="(country, idx) in zone.countries.slice(0, 2)"
                        :key="`country-${idx}`"
                        class="inline-flex items-center gap-1 rounded-md bg-n-green-2/40 px-2 py-0.5 text-xs text-n-green-11"
                      >
                        <Icon icon="i-lucide-globe" class="size-3" />
                        {{ country }}
                      </span>
                      <span
                        v-if="zone.countries.length > 2"
                        class="inline-flex items-center rounded-md bg-n-green-2/40 px-2 py-0.5 text-xs font-medium text-n-green-11"
                        :title="cellTitle(zone.countries)"
                      >
                        {{
                          t('TAXIS.COMMON.MORE_COUNT', {
                            count: zone.countries.length - 2,
                          })
                        }}
                      </span>
                    </div>
                    <span v-else class="text-n-slate-10">{{
                      t('TAXIS.HISTORY.DASH')
                    }}</span>
                  </td>
                  <td class="px-4 py-3.5">
                    <div
                      v-if="zone.postal_codes?.length"
                      class="flex flex-wrap gap-1"
                    >
                      <span
                        v-for="(code, idx) in zone.postal_codes.slice(0, 3)"
                        :key="`code-${idx}`"
                        class="inline-flex items-center gap-1 rounded-md bg-n-teal-2/40 px-2 py-0.5 font-mono text-xs text-n-teal-11"
                      >
                        <Icon icon="i-lucide-mail" class="size-3" />
                        {{ code }}
                      </span>
                      <span
                        v-if="zone.postal_codes.length > 3"
                        class="inline-flex items-center rounded-md bg-n-teal-2/40 px-2 py-0.5 text-xs font-medium text-n-teal-11"
                        :title="cellTitle(zone.postal_codes)"
                      >
                        {{
                          t('TAXIS.COMMON.MORE_COUNT', {
                            count: zone.postal_codes.length - 3,
                          })
                        }}
                      </span>
                    </div>
                    <span v-else class="text-n-slate-10">{{
                      t('TAXIS.HISTORY.DASH')
                    }}</span>
                  </td>
                  <td class="px-4 py-3 text-end">
                    <div class="flex justify-end gap-1">
                      <button
                        type="button"
                        class="rounded-lg p-2 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                        :title="t('TAXIS.ZONES.EDIT')"
                        @click="openEdit(zone)"
                      >
                        <Icon icon="i-lucide-pencil" class="size-4" />
                      </button>
                      <button
                        type="button"
                        class="rounded-lg p-2 text-n-ruby-11 hover:bg-n-ruby-3/30"
                        :title="t('TAXIS.ZONES.DELETE')"
                        @click="removeZone(zone)"
                      >
                        <Icon icon="i-lucide-trash-2" class="size-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-if="zones.length && !filteredZones.length"
            class="px-4 py-8 text-center text-sm text-n-slate-11"
          >
            {{ t('TAXIS.ZONES.NO_SEARCH_RESULTS') }}
          </div>
        </div>
      </template>
    </div>

    <Dialog
      ref="zoneDialogRef"
      type="edit"
      :title="
        editing
          ? t('TAXIS.ZONES.DIALOG_EDIT_TITLE')
          : t('TAXIS.ZONES.DIALOG_CREATE_TITLE')
      "
      width="lg"
      :show-cancel-button="false"
      :show-confirm-button="false"
      overflow-y-auto
      @close="onDialogClose"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="form.name"
          :label="t('TAXIS.ZONES.FORM.NAME')"
          :placeholder="t('TAXIS.ZONES.FORM.NAME_PLACEHOLDER')"
        />

        <p class="text-xs text-n-slate-10">
          {{ t('TAXIS.ZONES.FORM.MULTI_HELP') }}
        </p>

        <div
          v-for="cfg in multiFieldBlocks"
          :key="cfg.key"
          class="flex flex-col gap-1.5"
        >
          <label class="text-sm font-medium text-n-slate-12">
            {{ cfg.label }}
          </label>
          <div
            class="flex flex-col gap-2 rounded-lg border border-n-weak px-3 py-3 focus-within:border-n-blue-9"
          >
            <div v-if="form[cfg.key].length" class="flex flex-wrap gap-2">
              <span
                v-for="(item, index) in form[cfg.key]"
                :key="`${cfg.key}-${index}-${item}`"
                class="inline-flex max-w-full items-center gap-1 rounded-lg bg-n-alpha-2 py-1 pl-2 pr-1 text-sm text-n-slate-12"
              >
                <span class="min-w-0 truncate">{{ item }}</span>
                <button
                  type="button"
                  class="flex size-7 shrink-0 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-solid-3 hover:text-n-slate-12"
                  :title="t('TAXIS.ZONES.REMOVE_VALUE')"
                  @click="removeMultiValue(cfg.key, index)"
                >
                  <Icon icon="i-lucide-x" class="size-3.5" />
                </button>
              </span>
            </div>
            <div class="flex flex-wrap items-stretch gap-2">
              <Input
                v-model="pendingMulti[cfg.key]"
                class="min-w-[12rem] flex-1"
                :placeholder="cfg.placeholder"
                :custom-input-class="['h-9']"
                @enter.prevent="addPendingValue(cfg.key)"
              />
              <Button
                type="button"
                icon="i-lucide-plus"
                variant="faded"
                color="slate"
                size="sm"
                :label="t('TAXIS.ZONES.ADD_VALUE')"
                class="shrink-0"
                @click="addPendingValue(cfg.key)"
              />
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex w-full justify-end gap-2">
          <Button
            variant="faded"
            color="slate"
            type="button"
            :label="t('TAXIS.ZONES.CANCEL')"
            @click="closeDialog"
          />
          <Button
            type="button"
            :label="t('TAXIS.ZONES.SAVE')"
            @click="saveZone"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
